import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import type { SlidePoint } from '../../types/api';
import {
  clampFraction,
  NUDGE_STEP,
  NUDGE_STEP_LARGE,
  SNAP_TOLERANCE,
  roundFraction,
} from './slideGeometry';

/** Which centre lines the block is currently sitting on. */
export interface SnapGuides {
  x: boolean;
  y: boolean;
}

const NO_GUIDES: SnapGuides = { x: false, y: false };

/** Spread onto the draggable element. Typed for the div the canvas renders. */
export interface LayerDragHandlers {
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

export interface LayerDragResult {
  handlers: LayerDragHandlers;
  isDragging: boolean;
  guides: SnapGuides;
  /**
   * The arrow keys' own move: shifts `element` (the layer) by a fraction of the
   * stage and commits it. The nudge pad calls exactly this. False when nothing
   * moved (disabled, or the stage has no size yet).
   */
  nudge: (element: HTMLElement, dx: number, dy: number) => boolean;
}

/** The block's size as a fraction of the stage. */
interface Footprint {
  width: number;
  height: number;
}

/**
 * Everything one drag needs, measured once when the pointer goes down.
 *
 * Neither the stage's box nor the block's own size can change while a pointer
 * is held on it — a resize is the one thing that could, and that ends the drag
 * — so nothing after `pointerdown` reads the layout again.
 */
interface DragSession {
  element: HTMLElement;
  pointerId: number;
  /** The stage's client box. */
  stageLeft: number;
  stageTop: number;
  stageWidth: number;
  stageHeight: number;
  footprint: Footprint;
  /** Where the block started, as a fraction of the stage. */
  originX: number;
  originY: number;
  /** Where inside the block the pointer grabbed it, in px. */
  grabX: number;
  grabY: number;
  /**
   * Whatever transform the layout already had on the block. A template-placed
   * caption centres itself with `translateY(-50%)`; the drag composes on top of
   * it rather than overwriting it.
   */
  baseTransform: string;
  /** Latest clamped position: what the next frame paints and pointerup commits. */
  latest: SlidePoint | null;
  /** Latest snap flags, published on the same frame as the paint. */
  snap: SnapGuides;
  /** The one queued frame, or null when none is pending. */
  frame: number | null;
}

function measureFootprint(element: HTMLElement, stage: HTMLElement): Footprint | null {
  const stageRect = stage.getBoundingClientRect();
  if (stageRect.width === 0 || stageRect.height === 0) return null;
  const elementRect = element.getBoundingClientRect();
  return {
    width: elementRect.width / stageRect.width,
    height: elementRect.height / stageRect.height,
  };
}

/**
 * Direct manipulation for one layer of the slide canvas.
 *
 * Pointer events rather than mouse events, with `setPointerCapture`, so a drag
 * survives the pointer leaving the block and works the same under a finger.
 *
 * The drag itself never goes through React. `pointerdown` measures the stage
 * and the block once; every `pointermove` after it is pure arithmetic that
 * parks a clamped fraction on the session, and a single `requestAnimationFrame`
 * writes it to the element as a CSS transform — one style write per frame,
 * latest event wins, no layout read and no `setState`. The committed value is
 * the position the user last saw, clamped and rounded by exactly the same
 * `clampFraction` every pointermove used to call, so what crosses the wire is
 * unchanged; only the number of React renders is (one, on pointerup, instead of
 * one per pointer event).
 *
 * Two accessibility obligations are met here rather than bolted on: the block
 * is focusable and moves under the arrow keys (1% a press, 5% with Shift) —
 * committing immediately, since a nudge is a decision rather than a gesture —
 * and it snaps to either centre line so a keyboard-free user can still centre
 * something exactly.
 */
export function useLayerDrag(
  stageRef: RefObject<HTMLDivElement | null>,
  point: SlidePoint,
  onChange: (next: SlidePoint) => void,
  disabled = false,
  /**
   * False leaves a pointer press to the page. Under a finger an unselected
   * layer is not armed: the touch can scroll past it, and the tap selects it
   * (it focuses), so only a selected layer takes the drag. The keyboard and the
   * nudge are never gated by it.
   */
  armed = true,
): LayerDragResult {
  const [isDragging, setIsDragging] = useState(false);
  const [guides, setGuides] = useState<SnapGuides>(NO_GUIDES);
  const sessionRef = useRef<DragSession | null>(null);
  // The commit fires from a window listener as well as from pointerup, so the
  // current callback is read through a ref rather than captured by the effect.
  const changeRef = useRef(onChange);

  useEffect(() => {
    changeRef.current = onChange;
  }, [onChange]);

  /** The one write per frame. Reads nothing off the DOM. */
  const paint = useCallback(() => {
    const session = sessionRef.current;
    if (session === null) return;
    session.frame = null;
    const next = session.latest;
    if (next === null) return;

    const dx = (next.x - session.originX) * session.stageWidth;
    const dy = (next.y - session.originY) * session.stageHeight;
    session.element.style.transform =
      `translate(${dx}px, ${dy}px) ${session.baseTransform}`.trimEnd();

    // The guides are the one thing on this path that React draws, and they
    // only ever change when the block crosses a snap threshold.
    const snap = session.snap;
    setGuides((current) => (current.x === snap.x && current.y === snap.y ? current : { ...snap }));
  }, []);

  /**
   * Ends the drag: the inline transform goes, and the position it represented
   * is committed through `onChange` as a normal state update.
   */
  const finish = useCallback((commit: boolean) => {
    const session = sessionRef.current;
    if (session === null) return;
    sessionRef.current = null;

    if (session.frame !== null) cancelAnimationFrame(session.frame);
    session.element.style.transform = session.baseTransform;
    if (session.element.hasPointerCapture(session.pointerId)) {
      session.element.releasePointerCapture(session.pointerId);
    }
    setIsDragging(false);
    setGuides(NO_GUIDES);

    // Both DOM writes land in the same task, so the block never paints back at
    // its old position between clearing the transform and the re-render.
    if (commit && session.latest !== null) changeRef.current(session.latest);
  }, []);

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (disabled || !armed || event.button !== 0) return;
    const stage = stageRef.current;
    if (!stage) return;

    // The only two layout reads of the whole drag.
    const stageRect = stage.getBoundingClientRect();
    if (stageRect.width === 0 || stageRect.height === 0) return;
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();

    sessionRef.current = {
      element,
      pointerId: event.pointerId,
      stageLeft: stageRect.left,
      stageTop: stageRect.top,
      stageWidth: stageRect.width,
      stageHeight: stageRect.height,
      footprint: {
        width: rect.width / stageRect.width,
        height: rect.height / stageRect.height,
      },
      originX: (rect.left - stageRect.left) / stageRect.width,
      originY: (rect.top - stageRect.top) / stageRect.height,
      grabX: event.clientX - rect.left,
      grabY: event.clientY - rect.top,
      baseTransform: element.style.transform,
      latest: null,
      snap: NO_GUIDES,
      frame: null,
    };

    element.setPointerCapture(event.pointerId);
    // preventDefault suppresses the browser's own focus and text selection, so
    // focus is moved deliberately — the block stays keyboard-nudgeable after a drag.
    event.preventDefault();
    element.focus();
    setIsDragging(true);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const session = sessionRef.current;
    if (session === null) return;

    let x = (event.clientX - session.grabX - session.stageLeft) / session.stageWidth;
    let y = (event.clientY - session.grabY - session.stageTop) / session.stageHeight;

    // Centring is on the block's centre, not its corner.
    const centeredX = 0.5 - session.footprint.width / 2;
    const centeredY = 0.5 - session.footprint.height / 2;
    const snapX = Math.abs(x - centeredX) <= SNAP_TOLERANCE;
    const snapY = Math.abs(y - centeredY) <= SNAP_TOLERANCE;
    if (snapX) x = centeredX;
    if (snapY) y = centeredY;

    session.latest = {
      x: clampFraction(x, session.footprint.width),
      y: clampFraction(y, session.footprint.height),
    };
    session.snap = { x: snapX, y: snapY };
    if (session.frame === null) session.frame = requestAnimationFrame(paint);
  }

  /**
   * A resize invalidates the measurements this drag is standing on, so it ends
   * here. It ends rather than reverts: the last painted position is one the
   * user watched land, and throwing it away would be the surprising half.
   */
  useEffect(() => {
    if (!isDragging) return;

    function handleResize() {
      finish(true);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [finish, isDragging]);

  /** An unmount mid-drag drops the frame; it does not commit anything. */
  useEffect(
    () => () => {
      const session = sessionRef.current;
      if (session !== null && session.frame !== null) cancelAnimationFrame(session.frame);
      sessionRef.current = null;
    },
    [],
  );

  /**
   * One step of keyboard movement: one measurement, one commit. Nothing here
   * runs at frame rate. Shared by the arrow keys and the nudge pad.
   */
  function nudge(element: HTMLElement, dx: number, dy: number): boolean {
    if (disabled) return false;
    const stage = stageRef.current;
    if (!stage) return false;
    const footprint = measureFootprint(element, stage);
    if (!footprint) return false;
    onChange({
      x: clampFraction(roundFraction(point.x) + dx, footprint.width),
      y: clampFraction(roundFraction(point.y) + dy, footprint.height),
    });
    return true;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;

    const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
    let dx = 0;
    let dy = 0;
    switch (event.key) {
      case 'ArrowLeft':
        dx = -step;
        break;
      case 'ArrowRight':
        dx = step;
        break;
      case 'ArrowUp':
        dy = -step;
        break;
      case 'ArrowDown':
        dy = step;
        break;
      default:
        return;
    }

    if (!nudge(event.currentTarget, dx, dy)) return;
    // The strip above also answers to the arrow keys; a focused layer keeps them.
    event.preventDefault();
    event.stopPropagation();
  }

  return {
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: () => finish(true),
      onPointerCancel: () => finish(true),
      onKeyDown: handleKeyDown,
    },
    isDragging,
    guides,
    nudge,
  };
}
